import React from 'react'
import { useParams, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import SEO, { SITE_URL } from '../components/SEO'
import { getPostBySlug, blogPosts } from '../content/blogposts'
import theme from '../theme'

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const history = useHistory()
  const post = getPostBySlug(slug)

  if (!post) {
    history.push('/404')
    return null
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": { "@type": "Organization", "name": "MiAmigo.AI" },
    "publisher": {
      "@type": "Organization",
      "name": "MiAmigo.AI",
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/android-chrome-512x512.png` },
    },
    "mainEntityOfPage": `${SITE_URL}/blog/${post.slug}`,
  }

  return (
    <PageContainer>
      <SEO
        title={`${post.title} | MiAmigo.AI Blog`}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Article>
        <BackLink onClick={() => history.push('/blog')}>&larr; All posts</BackLink>
        <PostDate>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</PostDate>
        <Title>{post.title}</Title>
        {post.content.map((block, i) => {
          if (block.type === 'h2') return <SectionHeading key={i}>{block.text}</SectionHeading>
          if (block.type === 'ul') return (
            <List key={i}>
              {block.items?.map((item, j) => <li key={j}>{item}</li>)}
            </List>
          )
          return <Paragraph key={i}>{block.text}</Paragraph>
        })}

        <CTA>
          <CTAText>See it answer questions about your own website.</CTAText>
          <CTAButton href="/register">Get Your Chatbot →</CTAButton>
        </CTA>
      </Article>

      <MorePosts>
        <MoreHeading>More posts</MoreHeading>
        {blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3).map((p) => (
          <MoreLink key={p.slug} onClick={() => history.push(`/blog/${p.slug}`)}>{p.title}</MoreLink>
        ))}
      </MorePosts>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px 80px;
  min-height: 85vh;
`

const Article = styled.article`
  width: 100%;
  max-width: 700px;
  color: ${theme.colors.text};
`

const BackLink = styled.div`
  font-size: 13px;
  color: ${theme.colors.primary};
  cursor: pointer;
  margin-bottom: 16px;
  font-weight: 600;
`

const PostDate = styled.div`
  font-size: 13px;
  color: ${theme.colors.textMuted};
  margin-bottom: 6px;
`

const Title = styled.h1`
  font-size: 34px;
  font-weight: 900;
  line-height: 1.25;
  margin: 0 0 24px 0;
`

const SectionHeading = styled.h2`
  font-size: 22px;
  font-weight: 800;
  margin: 32px 0 12px 0;
`

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: ${theme.colors.text};
  margin: 0 0 16px 0;
`

const List = styled.ul`
  margin: 0 0 16px 0;
  padding-left: 22px;
  li {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 6px;
  }
`

const CTA = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-top: 40px;
  padding: 24px;
  background: ${theme.colors.primarySoft};
  border-radius: ${theme.radius.lg};
`

const CTAText = styled.div`
  font-size: 16px;
  font-weight: 600;
`

const CTAButton = styled.a`
  display: inline-block;
  padding: 10px 20px;
  border-radius: ${theme.radius.md};
  background: ${theme.colors.primary};
  color: #fff;
  font-weight: 700;
  text-decoration: none;
`

const MorePosts = styled.div`
  width: 100%;
  max-width: 700px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid ${theme.colors.border};
`

const MoreHeading = styled.div`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: 12px;
`

const MoreLink = styled.div`
  font-size: 15px;
  color: ${theme.colors.primary};
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 10px;
`

export default BlogPost
